import { useEffect, useMemo, useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Trash2, Star, ShieldCheck, Eye, EyeOff, MessageSquareText, Award } from "lucide-react";

const TABS = [
  { key: "pending", label: "En attente", filter: (r) => !r.published },
  { key: "published", label: "Publiés", filter: (r) => r.published },
  { key: "all", label: "Tous", filter: () => true },
];

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
  } catch { return ""; }
};

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/reviews");
      setReviews(r.data.reviews);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReviews(); }, []);

  const counts = useMemo(() => ({
    pending: reviews.filter((r) => !r.published).length,
    published: reviews.filter((r) => r.published).length,
    all: reviews.length,
  }), [reviews]);

  const filtered = useMemo(() => {
    const f = TABS.find((t) => t.key === tab)?.filter || (() => true);
    return reviews.filter(f);
  }, [reviews, tab]);

  const patch = async (id, body, okMsg) => {
    try {
      await adminApi.patch(`/admin/reviews/${id}`, body);
      toast.success(okMsg);
      fetchReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cet avis ?")) return;
    try {
      await adminApi.delete(`/admin/reviews/${id}`);
      setReviews((r) => r.filter((x) => x.id !== id));
      toast.success("Avis supprimé");
    } catch {
      toast.error("Erreur");
    }
  };

  return (
    <div data-testid="admin-reviews-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl">Avis clients</h1>
          <p className="text-amyu-muted mt-1">Vérifie, publie ou masque les avis reçus des clients ♡</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`reviews-tab-${t.key}`}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-all ${
                tab === t.key
                  ? "bg-coral text-white shadow-[0_4px_12px_rgba(229,91,91,0.3)]"
                  : "bg-white text-amyu-ink hover:bg-amyu-cream"
              }`}
            >
              {t.label}
              <span className={`ml-1.5 text-[11px] ${tab === t.key ? "text-white/80" : "text-amyu-muted"}`}>
                {counts[t.key]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-coral" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-amyu-muted bg-white rounded-3xl py-14 mt-6" data-testid="reviews-empty">
          <MessageSquareText className="mx-auto text-amyu-muted/50 mb-2" size={28} />
          Aucun avis dans cette catégorie
        </div>
      ) : (
        <div className="mt-6 grid gap-3">
          {filtered.map((r) => (
            <div
              key={r.id}
              data-testid={`review-card-${r.id}`}
              className={`bg-white rounded-2xl p-5 border-l-4 ${
                r.published ? "border-l-amyu-pistachio" : "border-l-coral/70"
              }`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-base">{r.author}</span>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star
                          key={i}
                          size={13}
                          className={i <= r.rating ? "text-coral fill-coral" : "text-amyu-muted/40"}
                        />
                      ))}
                    </div>
                    {r.card_id && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-amyu-pink/60 text-amyu-ink rounded-full">
                        <Award size={10} /> Carte fidélité
                      </span>
                    )}
                    {r.verified && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-amyu-pistachio/40 text-amyu-ink rounded-full">
                        <ShieldCheck size={10} /> Vérifié
                      </span>
                    )}
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                        r.published
                          ? "bg-amyu-pistachio/60 text-amyu-ink"
                          : "bg-coral/15 text-coral"
                      }`}
                    >
                      {r.published ? "Publié" : "En attente"}
                    </span>
                  </div>
                  <p className="text-sm mt-2.5 text-amyu-ink/80 leading-relaxed">« {r.text} »</p>
                  <div className="text-[11px] text-amyu-muted mt-2">
                    Reçu le {formatDate(r.created_at)}
                    {r.card_id && !r.published && " · +1 tampon offert à la publication"}
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-amyu-cream/70 flex items-center gap-2 flex-wrap">
                {!r.verified && (
                  <button
                    onClick={() => patch(r.id, { verified: true }, "Avis marqué comme vérifié")}
                    data-testid={`review-verify-${r.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amyu-cream hover:bg-amyu-pistachio/40 text-sm transition-colors"
                  >
                    <ShieldCheck size={13} /> Marquer vérifié
                  </button>
                )}
                {r.published ? (
                  <button
                    onClick={() => patch(r.id, { published: false }, "Avis masqué")}
                    data-testid={`review-unpublish-${r.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amyu-cream hover:bg-amyu-pink text-sm transition-colors"
                  >
                    <EyeOff size={13} /> Masquer
                  </button>
                ) : (
                  <button
                    onClick={() => patch(r.id, { published: true }, r.card_id ? "Avis publié · +1 tampon offert ♡" : "Avis publié")}
                    data-testid={`review-publish-${r.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-coral text-white hover:opacity-90 text-sm font-medium shadow-[0_4px_10px_rgba(229,91,91,0.25)] transition-all"
                  >
                    <Eye size={13} /> Publier
                  </button>
                )}
                <button
                  onClick={() => remove(r.id)}
                  data-testid={`review-delete-${r.id}`}
                  className="inline-flex items-center gap-1.5 ml-auto px-3 py-1.5 rounded-full text-amyu-muted hover:text-coral hover:bg-coral/10 text-sm transition-colors"
                >
                  <Trash2 size={13} /> Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
